import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { CreateDecal } from '@babylonjs/core/Meshes/Builders/decalBuilder';
import { Color3, Vector3 } from '@babylonjs/core/Maths/math';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import clone from 'lodash/clone';

import { PatternMesh } from '../Mesh/patternMesh';
import { SystemLayer } from '../../../System/systemLayer';
import { DegreeToRadian } from '../../../../Utils/math';

export type DecalType = 'text' | 'mask'

export interface IVariantDecal {
    name: string,
    meshes: string[],
    type: DecalType
}

export interface IDecalGeometry {
    position: Vector3,
    normal: Vector3,
    rotation: number,
    size: number,
}

export interface IDecalParameters extends IVariantDecal, IDecalGeometry {
    sourceMeshName?: string,
    sourceMeshStayVisible?: boolean,
}

const scaleTexture = (texture: Texture, scale: number): void => {
    const realScale = 1 / scale;
    texture.uScale = realScale;
    texture.vScale = realScale;
    const realOffset = (-0.5 * realScale) + 0.5;
    texture.uOffset = realOffset;
    texture.vOffset = realOffset;
};

export abstract class PatternDecal extends PatternMesh {
    public material: PBRMaterial

    protected sourceMesh: AbstractMesh

    protected geometry: IDecalParameters = {}

    public abstract setGeometry(decalPar: IDecalParameters): void

    public abstract getGeometry(): IDecalParameters

    public abstract resetGeometry(): void

    public setGeometryName(name: string): void {
        this.geometry.name = name;
    }

    public meshes: string[]

    constructor(systemLayer: SystemLayer, meshes: string[]) {
        super(systemLayer);
        this.meshes = meshes;

        this.buildMaterial();
        this.setEditPositionEvent();
    }

    private buildMaterial() {
        this.material = new PBRMaterial('', this._system.scene);
        this.material.alphaMode = 2;
        this.material.transparencyMode = 2;
        this.material.useRadianceOverAlpha = false;
        this.material.useSpecularOverAlpha = false;
        this.material.zOffset = -2;
        this.material.albedoColor = new Color3(1, 1, 1);
    }

    public setSourceMesh(sourceMesh: AbstractMesh): void {
        if (sourceMesh !== this.sourceMesh) this.setSourceEmissive(false);
        this.sourceMesh = sourceMesh;
        this.geometry.sourceMeshName = sourceMesh.name;
        if (this.editPositionActivated) this.setSourceEmissive(true);
    }

    private setSourceEmissive(emissive: boolean): void {
        if (!this.sourceMesh) return;
        const mat = this.sourceMesh.material as PBRMaterial;
        if (emissive) {
            mat.emissiveColor = new Color3(0.02, 0.02, 0.02);
        } else {
            mat.emissiveColor = Color3.Black();
        }
    }

    private setMeshPickable(pickable: boolean): void {
        if (this.mesh) this.mesh.isPickable = pickable;
    }

    protected abstract currentVariant: IVariantDecal

    public abstract setVariant(variant: IVariantDecal): void

    public abstract resetVariant(): void

    public getVariant(): IVariantDecal {
        return clone(this.currentVariant);
    }

    private position: Vector3

    private normal: Vector3

    protected abstract highlight(highlight: boolean): void

    protected createDecal(position: Vector3, normal: Vector3): void {
        this.position = position;
        this.normal = normal;
        this.geometry.position = position;
        this.geometry.normal = normal;

        if (!this.sourceMesh) return;
        if (this.mesh) this.mesh.dispose();

        const decalSize = Vector3.One();
        const name = `Decal_${this.sourceMesh.name}`;
        const mesh = CreateDecal(
            name,
            this.sourceMesh,
            {
                position, normal, size: decalSize, cullBackFaces: true
            }
        );
        mesh.material = this.material;
        this._system.checkActiveMeshes();
        this.mesh = mesh;
        this.setMeshPickable(false);
        //! Can't use setMesh as it sets the mesh position
        // this._setMesh(mesh)
    }

    //* Attemp to update decal without recreated it but need also vertex update
    // this.mesh.position = position;
    // const yaw = -Math.atan2(normal.z, normal.x) - Math.PI / 2;
    // const len = Math.sqrt(normal.x * normal.x + normal.z * normal.z);
    // const pitch = Math.atan2(normal.y, len);
    // // this.mesh.normal = normal
    // this.mesh.rotation = new Vector3(pitch, yaw, 0);

    public resetDecalGeometry(): void {
        this.setDecalGeometry({
            position: this.position,
            normal: this.normal,
            rotation: this.geometry.rotation,
            size: this.geometry.size
        });
    }

    protected setDecalParameters(geometry: IDecalParameters): void {
        this.geometry.sourceMeshStayVisible = geometry.sourceMeshStayVisible;
        this.geometry.sourceMeshName = geometry.sourceMeshName;
        this.geometry.name = geometry.name;
        this.geometry.type = geometry.type;
        this.setDecalGeometry(geometry);
    }

    protected setDecalGeometry(geometry: IDecalGeometry): void {
        this.createDecal(geometry.position.clone(), geometry.normal.clone());
        this.setDecalRotation(geometry.rotation);
        this.setDecalSize(geometry.size);
    }

    editPositionActivated = false

    public setEditPositionEvent(): void {
        let start = false;
        this._system.canvas.addEventListener('pointerdown', (evt) => {
            if (this.editPositionActivated && this.isDecalPicked(evt)) {
                start = true;
                this.setDecalOnMove(evt);
            }
        });

        this._system.canvas.addEventListener('pointerup', (evt) => {
            if (this.editPositionActivated) this.setMeshPickable(true);
            start = false;
            this.decalMoving = false;
        });

        this._system.canvas.addEventListener('pointermove', (evt) => {
            // check if we are under a mesh
            if (start && this.editPositionActivated) {
                this.setDecalOnMove(evt);
            }
        });
    }

    private isDecalPicked(evt): boolean {
        // If no mesh yet, we need to create one
        if (!this.mesh) return true;
        const pickInfo = this._system.pick(evt, (m) => m === this.mesh);
        return pickInfo.hit;
    }

    public setEditPositionMode(activate: boolean): void {
        this.editPositionActivated = activate;
        this.setMeshPickable(activate);
        this.setSourceEmissive(activate);
        this.highlight(activate);
    }

    private getSourceMesh(evt: Event) {
        const pickInfo = this._system.pick(evt);
        if (pickInfo.hit) {
            this.setSourceMesh(pickInfo.pickedMesh);
        }
    }

    public decalMoving = false;

    private setDecalOnMove(evt: Event): void {
        this.setMeshPickable(false);
        this.getSourceMesh(evt);
        const pickInfo = this._system.pick(evt, (m) => m === this.sourceMesh);
        if (pickInfo.hit) {
            this.decalMoving = true;
            const position = pickInfo.pickedPoint;
            const normal = pickInfo.getNormal(true);
            this.setDecalGeometry({
                position,
                normal,
                rotation: this.geometry.rotation,
                size: this.geometry.size
            });
        }
    }

    protected decalSize: number

    public setDecalSize(decalSize: number): void {
        this.decalSize = decalSize;
        this.geometry.size = decalSize;
        const m = this.material;
        if (m.opacityTexture) scaleTexture(m.opacityTexture as Texture, decalSize);
        this.updateDecal();
    }

    protected decalRotation: number

    public setDecalRotation(rotation: number): void {
        this.geometry.rotation = rotation;
        this.decalRotation = rotation * DegreeToRadian;
        const opacityTexture = this.material.opacityTexture as Texture;
        if (opacityTexture) opacityTexture.wAng = this.decalRotation;
        this.updateDecal();
    }

    public setMaterial(material: string): void {
        this._setMaterial(material);
    }

    protected _setMaterial(materialKey: string): void {
        const material = this._system.toolMaterial.getMaterialByKey(materialKey);
        this.applyMaterial(material);
        this.updateDecal();
    }

    protected updateDecal(): void {
        this._system.checkActiveMeshes();
        this._system.scene.render();
    }
}
